import {
  collectionGroup,
  query,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
  getDoc,
  orderBy,
  limit,
} from "firebase/firestore";
import { db } from "../config/firebase";
import type { Offer } from "../types/offer.types";

export interface OfferWithBar extends Offer {
  barName: string;
  status?: "active" | "expired" | "paused";
}

export interface OfferStats {
  totalOffers: number;
  activeOffers: number;
  expiredOffers: number;
  pausedOffers: number;
}

/**
 * Lädt alle Offers über alle Bars
 */
export const getAllOffers = async (): Promise<OfferWithBar[]> => {
  try {
    const offersQuery = query(
      collectionGroup(db, "offers"),
      orderBy("validFrom", "desc"),
      limit(100)
    );

    const snapshot = await getDocs(offersQuery);
    const offers: OfferWithBar[] = [];

    for (const offerDoc of snapshot.docs) {
      const offerData = offerDoc.data();
      const barId = offerDoc.ref.parent.parent?.id || "";

      // Lade Bar-Namen
      let barName = "Unknown Bar";
      if (barId) {
        const barDoc = await getDoc(doc(db, "bars", barId));
        if (barDoc.exists()) {
          barName = barDoc.data().name || barId;
        }
      }

      // Automatischer Status basierend auf Datum
      const now = Timestamp.now();
      const validFrom = offerData.validFrom;
      const validUntil = offerData.validUntil;

      let status: "active" | "expired" | "paused" = "active";
      if (!offerData.isPublished) {
        status = "paused";
      } else if (validUntil && validUntil.toDate() < now.toDate()) {
        status = "expired";
      } else if (validFrom && validFrom.toDate() > now.toDate()) {
        status = "paused"; // Noch nicht gestartet
      }

      offers.push({
        id: offerDoc.id,
        barId,
        barName,
        title: offerData.title,
        description: offerData.description,
        type: offerData.type,
        discountType: offerData.discountType,
        discountValue: offerData.discountValue,
        validFrom: offerData.validFrom,
        validUntil: offerData.validUntil,
        daysOfWeek: offerData.daysOfWeek || [],
        timeSlots: offerData.timeSlots || [],
        imageUrl: offerData.imageUrl,
        applicableDrinks: offerData.applicableDrinks || [],
        isPublished: offerData.isPublished !== false,
        createdBy: offerData.createdBy || "",
        createdAt: offerData.createdAt,
        updatedAt: offerData.updatedAt || offerData.createdAt,
        status,
      });
    }

    return offers;
  } catch (error) {
    console.error("Error fetching all offers:", error);
    throw error;
  }
};

/**
 * Lädt Offers nach Status-Filter
 */
export const getOffersByStatus = async (
  status: "active" | "expired" | "paused"
): Promise<OfferWithBar[]> => {
  try {
    const allOffers = await getAllOffers();
    return allOffers.filter((offer) => offer.status === status);
  } catch (error) {
    console.error("Error fetching offers by status:", error);
    throw error;
  }
};

/**
 * Lädt alle Offers für eine spezifische Bar
 */
export const getOffersByBar = async (
  barId: string
): Promise<OfferWithBar[]> => {
  try {
    const allOffers = await getAllOffers();
    return allOffers.filter((offer) => offer.barId === barId);
  } catch (error) {
    console.error("Error fetching offers by bar:", error);
    throw error;
  }
};

/**
 * Lädt alle Bars die Offers haben (nur geclaimte Bars)
 */
export const getBarsWithOffers = async (): Promise<
  Array<{
    barId: string;
    barName: string;
    offerCount: number;
    isClaimed: boolean;
  }>
> => {
  try {
    const allOffers = await getAllOffers();

    // Gruppiere Offers nach barId
    const barMap = new Map<string, { barName: string; count: number }>();

    for (const offer of allOffers) {
      if (offer.barId) {
        const existing = barMap.get(offer.barId);
        if (existing) {
          existing.count++;
        } else {
          barMap.set(offer.barId, { barName: offer.barName, count: 1 });
        }
      }
    }

    // Konvertiere zu Array und prüfe isClaimed
    const barsWithOffers = [];
    for (const [barId, data] of barMap.entries()) {
      const barDoc = await getDoc(doc(db, "bars", barId));
      if (barDoc.exists() && barDoc.data().isClaimed === true) {
        barsWithOffers.push({
          barId,
          barName: data.barName,
          offerCount: data.count,
          isClaimed: true,
        });
      }
    }

    return barsWithOffers.sort((a, b) => b.offerCount - a.offerCount);
  } catch (error) {
    console.error("Error fetching bars with offers:", error);
    throw error;
  }
};

/**
 * Pausiert/Unpublisht ein Offer
 */
export const toggleOfferPublish = async (
  barId: string,
  offerId: string,
  isPublished: boolean
): Promise<void> => {
  try {
    const offerRef = doc(db, "bars", barId, "offers", offerId);
    await updateDoc(offerRef, {
      isPublished,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error toggling offer publish:", error);
    throw error;
  }
};

/**
 * Löscht ein Offer
 */
export const deleteOffer = async (
  barId: string,
  offerId: string
): Promise<void> => {
  try {
    const offerRef = doc(db, "bars", barId, "offers", offerId);
    await deleteDoc(offerRef);
  } catch (error) {
    console.error("Error deleting offer:", error);
    throw error;
  }
};

/**
 * Lädt Offer Stats
 */
export const getOfferStats = async (): Promise<OfferStats> => {
  try {
    const allOffers = await getAllOffers();

    const activeOffers = allOffers.filter((o) => o.status === "active").length;
    const expiredOffers = allOffers.filter(
      (o) => o.status === "expired"
    ).length;
    const pausedOffers = allOffers.filter((o) => o.status === "paused").length;

    return {
      totalOffers: allOffers.length,
      activeOffers,
      expiredOffers,
      pausedOffers,
    };
  } catch (error) {
    console.error("Error fetching offer stats:", error);
    return {
      totalOffers: 0,
      activeOffers: 0,
      expiredOffers: 0,
      pausedOffers: 0,
    };
  }
};
